import { useEffect, useState, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, fmtEUR } from "@/lib/api";
import { ArrowLeft, Loader2, ShoppingCart, Printer } from "lucide-react";
import ProductCard from "@/components/ProductCard";

export default function PrinterLanding() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        api.get(`/printers/${slug}`)
            .then((r) => setData(r.data))
            .catch((e) => {
                if (e?.response?.status === 404) setNotFound(true);
            })
            .finally(() => setLoading(false));
    }, [slug]);

    const minPrice = useMemo(() => {
        if (!data?.products?.length) return null;
        return data.products.reduce((m, p) => Math.min(m, p.final_price ?? p.price), Infinity);
    }, [data]);

    if (notFound) return <Navigate to="/impressoras" replace />;

    if (loading) {
        return (
            <div className="min-h-[40vh] flex items-center justify-center font-mono">
                <Loader2 className="animate-spin" size={20} />
            </div>
        );
    }

    if (!data) return null;
    const { printer, products, count } = data;
    const seoTitle = `Toner & Tinteiros ${printer.name} | SOSTONERS`;
    const seoDesc = `${count} consumíveis compatíveis e originais para ${printer.name}. Preços desde ${minPrice != null && minPrice !== Infinity ? fmtEUR(minPrice) : "—"}. Stock disponível, envio em 24h. Garantia 24 meses.`;

    return (
        <div className="container-edge py-10" data-testid="printer-landing">
            <Helmet>
                <title>{seoTitle}</title>
                <meta name="description" content={seoDesc} />
                <link rel="canonical" href={`https://sostoners.pt/impressora/${slug}`} />
                <meta property="og:title" content={seoTitle} />
                <meta property="og:description" content={seoDesc} />
                <meta property="og:type" content="website" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "ItemList",
                        "name": `Consumíveis para ${printer.name}`,
                        "numberOfItems": count,
                        "itemListElement": products.slice(0, 30).map((p, i) => ({
                            "@type": "ListItem",
                            "position": i + 1,
                            "item": {
                                "@type": "Product",
                                "name": p.name,
                                "sku": p.sku,
                                "image": p.image_url,
                                "offers": {
                                    "@type": "Offer",
                                    "price": (p.final_price ?? p.price).toFixed(2),
                                    "priceCurrency": "EUR",
                                    "availability": (p.stock || 0) > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                                },
                            },
                        })),
                    })}
                </script>
            </Helmet>

            {/* Breadcrumb */}
            <nav className="text-xs font-mono text-ink/60 mb-6" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-magenta">Início</Link>
                <span className="mx-2">/</span>
                <Link to="/impressoras" className="hover:text-magenta">Impressoras</Link>
                <span className="mx-2">/</span>
                <span className="text-ink">{printer.name}</span>
            </nav>

            {/* Hero */}
            <header className="mb-10 border-2 border-ink bg-white p-8 lg:p-10 shadow-[8px_8px_0_0_#0A0A0A]">
                <div className="flex items-start gap-4 mb-4">
                    <div className="border-2 border-ink p-3 bg-paper">
                        <Printer size={28} />
                    </div>
                    <div className="flex-1">
                        <div className="label-mono text-magenta">{printer.brand}</div>
                        <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-1 leading-tight">
                            Toner & Tinteiros para<br/>
                            <span className="text-magenta">{printer.name}</span>
                        </h1>
                    </div>
                </div>
                <p className="text-base lg:text-lg text-ink/70 max-w-3xl mt-4">
                    <strong>{count} consumíveis compatíveis</strong> para a {printer.name}.
                    {minPrice != null && minPrice !== Infinity && <> Desde <strong className="text-magenta">{fmtEUR(minPrice)}</strong>.</>}
                    {" "}Originais e compatíveis premium, com garantia 24 meses e envio em 24h para Portugal Continental.
                </p>
                <div className="mt-6 flex flex-wrap gap-3 font-mono text-xs">
                    <span className="border-2 border-ink px-3 py-1.5">✓ Stock disponível</span>
                    <span className="border-2 border-ink px-3 py-1.5">✓ Envio 24h</span>
                    <span className="border-2 border-ink px-3 py-1.5">✓ Garantia 24 meses</span>
                    <span className="border-2 border-ink px-3 py-1.5 bg-magenta text-white">✓ IVA incluído</span>
                </div>
            </header>

            {/* Products grid */}
            {products.length === 0 ? (
                <div className="text-center py-20 border-2 border-dashed border-ink/30">
                    <ShoppingCart size={32} className="mx-auto mb-3 text-ink/30" />
                    <p className="font-mono text-ink/60">Sem produtos disponíveis no momento.</p>
                </div>
            ) : (
                <>
                    <h2 className="font-display font-black text-2xl mb-5">Produtos compatíveis</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                        {products.map((p) => (
                            <ProductCard key={p.id} product={p} />
                        ))}
                    </div>
                </>
            )}

            {/* SEO content footer */}
            <section className="mt-14 prose prose-sm max-w-3xl">
                <h3 className="font-display font-black text-xl mb-3">Sobre os consumíveis para {printer.name}</h3>
                <p className="text-ink/75 leading-relaxed">
                    Comprar toner ou tinteiro para a sua <strong>{printer.name}</strong> nunca foi tão simples.
                    Na SOSTONERS oferecemos uma seleção cuidada de <strong>compatíveis premium</strong> com chip
                    actualizado e <strong>cartuchos originais {printer.brand}</strong>, todos testados na nossa oficina
                    técnica antes de seguirem para si.
                </p>
                <p className="text-ink/75 leading-relaxed mt-3">
                    Stock dinâmico actualizado várias vezes ao dia. Encomenda hoje, recebe amanhã (Portugal Continental).
                    Para empresas, oferecemos preços B2B personalizados — <Link to="/conta/registar" className="underline text-magenta">crie a sua conta</Link>{" "}
                    e contacte-nos para activar.
                </p>
            </section>

            <div className="mt-10">
                <Link to="/impressoras" className="text-sm font-mono text-ink/60 hover:text-magenta inline-flex items-center gap-2">
                    <ArrowLeft size={14} /> Ver todas as impressoras
                </Link>
            </div>
        </div>
    );
}
